import type { DedicatedUsage } from '../types/dedicatedServers'

export type DedicatedUsageRow = { index: number, time: number, bytes: number, values: Record<number, number> }

export function dedicatedUsageChart(data: DedicatedUsage, userIds: readonly number[] | null) {
  const step = data.interval_seconds * 1000
  const offset = 8 * 3600000
  const start = Math.floor((Date.parse(data.start_time) + offset) / step) * step - offset
  const end = Date.parse(data.end_time)
  const totals = new Map(data.members.map(member => [member.user_id, 0]))
  const values = new Map<number, Record<number, number>>()
  for (const point of data.points) {
    if (!totals.has(point.user_id) || !Number.isFinite(point.bytes) || point.bytes < 0) throw new Error('Invalid dedicated usage point')
    totals.set(point.user_id, totals.get(point.user_id)! + point.bytes)
    const time = Date.parse(point.time)
    const bucket = values.get(time) || {}
    bucket[point.user_id] = (bucket[point.user_id] || 0) + point.bytes
    values.set(time, bucket)
  }
  const members = [...data.members].sort((a, b) => a.user_id - b.user_id)
    .map((member, colorIndex) => ({ ...member, colorIndex, bytes: totals.get(member.user_id)! }))
  const selected = userIds === null ? null : new Set(userIds)
  const series = members.filter(member => selected === null ? member.bytes > 0 : selected.has(member.user_id))
  const rows: DedicatedUsageRow[] = []
  for (let time = start; time < end; time += step) {
    const bucket = values.get(time) || {}
    const bytes = series.reduce((sum, member) => sum + (bucket[member.user_id] || 0), 0)
    rows.push({ index: rows.length, time, bytes, values: bucket })
  }
  members.sort((a, b) => b.bytes - a.bytes || a.user_id - b.user_id)
  return { rows, members, series, total: members.reduce((sum, member) => sum + member.bytes, 0), selectedTotal: series.reduce((sum, member) => sum + member.bytes, 0) }
}

export function formatDedicatedBytes(bytes: number) {
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  const unit = bytes > 0 ? Math.max(0, Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1)) : 0
  return `${Number((bytes / 1024 ** unit).toFixed(2))} ${units[unit]}`
}
